import { string, bool, func } from 'prop-types';
import React, { useEffect, useRef } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import { useDispatch } from 'react-redux';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { plccApplyAnalytics, plccViewsAnalytics } from '../../actions/AnalyticsAction';
import { metaDataElement, metaDataProperty } from '../../utils/cmPreviewMetaData';

export const CMResponsiveImgConfig = {
    imageWidthPresets: {
        'nucleus-2': {
            small: '719',
            medium: '616',
            large: '696',
        },
        'nucleus-3': {
            small: '719',
            medium: '410',
            large: '464',
        },
        'nucleus-4': {
            small: '360',
            medium: '308',
            large: '348',
        },
        'nucleus-grid': {
            small: '360',
            medium: '308',
            large: '348',
        },
        'nucleus-carousel': {
            small: '360',
            medium: '308',
            large: '348',
        },
        'viz-nav-stacked': {
            small: '308',
            medium: '308',
            large: '308',
        },
        'viz-nav-carousel': {
            small: '308',
            medium: '308',
            large: '308',
        },
        'hero5-primary': {
            small: '462',
            medium: '554',
            large: '602',
        },
        'hero5-secondary': {
            small: '241',
            medium: '321',
            large: '379',
        },
        JCPStoryProduct: {
            small: {
                '1x1': '719',
                '5x7': '719',
                '7x5': '719',
            },
            medium: {
                '1x1': '608',
                '5x7': '560',
                '7x5': '738',
            },
            large: {
                '1x1': '646',
                '5x7': '552',
                '7x5': '832',
            },
        },
    },
};

const populateImageUrl = (absoluteURL, breakPoint, componentType, aspectRatio, imageType = '') => {
    const { imageWidthPresets } = CMResponsiveImgConfig;
    const component = isEmpty(imageType) ? componentType : `${componentType}-${imageType}`;
    const imageWidth = get(imageWidthPresets, `${component}.${breakPoint}`);
    const width = typeof imageWidth === 'object' ? imageWidth[aspectRatio?.slice(-3)] : imageWidth;
    return absoluteURL.replace('{cropName}', aspectRatio).replace('{width}', width);
};

const CMImageRenderer = (props) => {
    const plccAnalyticsTagName = get(props, 'data.preferences.plccAnalyticsTagName', 'PLCC');
    const analyticsId = get(props, 'analyticsId', '');
    const automationId = get(props, 'automationId', '');
    const imageRef = useRef();
    const dispatch = useDispatch();
    const smURL = populateImageUrl(
        props?.imageURL,
        'small',
        props?.componentType,
        props?.imageAspectRatio,
        props?.imageType
    );
    const mdURL = populateImageUrl(
        props?.imageURL,
        'medium',
        props?.componentType,
        props?.imageAspectRatio,
        props?.imageType
    );
    const lgURL = populateImageUrl(
        props?.imageURL,
        'large',
        props?.componentType,
        props?.imageAspectRatio,
        props?.imageType
    );
    const imageBannerStyle = dt(['w-full', 'object-cover']);
    let imageShape = '';
    const aspectSquareStyle =
        props?.itemPictureShape === 'round' && props?.imageAspectRatio !== 'portrait_ratio1x1'
            ? dt(['aspect-square'])
            : null;
    if (props?.itemPictureShape === 'square' && props?.imageAspectRatio === 'portrait_ratio1x1') {
        imageShape = dt(['aspect-square']);
    } else if (props?.itemPictureShape !== 'square') {
        imageShape = dt(['rounded-full']);
    } else {
        imageShape = dt(['aspect-auto']);
    }
    const imageOnLoad = (target) => {
        /* istanbul ignore next */
        const offsetHeight = get(target, 'target.offsetHeight', target.offsetHeight);
        props.setImageHeight && props.setImageHeight(offsetHeight);
    };

    const isPLCCBenefitsPage = () => {
        const plccBenefitPageURL = get(
            props,
            'data.preferences.plccBenefitsUrl',
            '/m/plcc-benefits'
        );
        return !__SERVER__ && window.location.href.indexOf(plccBenefitPageURL) > -1;
    };

    useEffect(() => {
        if (analyticsId.toUpperCase().includes(plccAnalyticsTagName)) {
            const payloadParams = {
                plccBanner: isPLCCBenefitsPage() ? 'static_rewards_apply' : 'static_home_apply',
            };
            dispatch(plccViewsAnalytics(payloadParams));
        }
        if (imageRef?.current?.complete) {
            imageOnLoad(imageRef?.current);
        }
    }, []);

    const applyPlccAnalytics = () => {
        const payloadParams = {
            plccBanner: isPLCCBenefitsPage() ? 'static_rewards_apply' : 'static_home_apply',
        };

        dispatch(plccApplyAnalytics(payloadParams));
    };

    const isPlccEnableLink = () => {
        return analyticsId.toUpperCase().includes(plccAnalyticsTagName);
    };

    return (
        <picture
            data-automation-id={automationId}
            data-cm-metadata={props?.isCMPreview && metaDataElement(props?.data, 'pictures')}
        >
            {/* small breakpoint */}
            <source media="(max-width: 767px)" srcSet={smURL} />
            {/* medium breakpoint */}
            <source media="(max-width: 1279px)" srcSet={mdURL} />
            {/* large breakpoint and IE */}
            <img
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...(isPlccEnableLink() && { onClick: applyPlccAnalytics })}
                alt={props.altText}
                className={[
                    imageBannerStyle,
                    imageShape,
                    props.imageStyles,
                    aspectSquareStyle,
                ].join(' ')}
                ref={imageRef}
                src={lgURL}
                data-cm-metadata={props?.isCMPreview && metaDataProperty('data')}
                onLoad={imageOnLoad}
            />
        </picture>
    );
};

CMImageRenderer.defaultProps = {
    altText: '',
    itemPictureShape: 'square',
    imageAspectRatio: '',
    componentType: '',
    imageURL: '',
    imageStyles: '',
    imageType: '',
    isCMPreview: false,
    setImageHeight: noop,
    analyticsId: '',
    automationId: '',
};

CMImageRenderer.propTypes = {
    altText: string,
    itemPictureShape: string,
    imageAspectRatio: string,
    componentType: string,
    imageURL: string,
    imageStyles: string,
    imageType: string,
    isCMPreview: bool,
    setImageHeight: func,
    analyticsId: string,
    automationId: string,
};

export default CMImageRenderer;
